import {
  MEDIA_UPLOAD_EXTENSION_TO_MIME_TYPE,
  MEDIA_UPLOAD_MIME_TYPE_TO_EXTENSION
} from '../../config/constants';

interface BrowserFileNameOptions {
  providedName?: string | null;
  blobType?: string | null;
  mimeType?: string | null;
  fallbackBaseName: string;
}

interface BrowserFileOptions extends BrowserFileNameOptions {
  lastModified?: number;
}

const inferExtensionFromName = (name: string) => {
  const cleanName = name.split('?')[0].split('#')[0];
  const extension = cleanName.split('.').pop()?.toLowerCase();

  return extension && MEDIA_UPLOAD_EXTENSION_TO_MIME_TYPE[extension]
    ? extension
    : null;
};

export const inferMimeFromFilename = (name?: string): string | undefined => {
  if (!name) return undefined;
  const extension = inferExtensionFromName(name);
  return extension ? MEDIA_UPLOAD_EXTENSION_TO_MIME_TYPE[extension] : undefined;
};

export const buildCloudPickerFileName = ({
  providedName,
  blobType,
  mimeType,
  fallbackBaseName
}: BrowserFileNameOptions) => {
  const baseName = providedName?.trim() || fallbackBaseName;
  const extensionFromName = inferExtensionFromName(baseName);
  const extensionFromMime =
    MEDIA_UPLOAD_MIME_TYPE_TO_EXTENSION[mimeType || blobType || ''];
  const extension = extensionFromName || extensionFromMime;
  const hasExtension = /\.[A-Za-z0-9]+$/.test(baseName);

  return hasExtension || !extension ? baseName : `${baseName}.${extension}`;
};

export const createBrowserFileFromBlob = (
  blob: Blob,
  {
    providedName,
    blobType,
    mimeType,
    fallbackBaseName,
    lastModified = Date.now()
  }: BrowserFileOptions
) => {
  const fileName = buildCloudPickerFileName({
    providedName,
    blobType,
    mimeType,
    fallbackBaseName
  });

  const effectiveType =
    blob.type ||
    mimeType ||
    (MEDIA_UPLOAD_EXTENSION_TO_MIME_TYPE[
      inferExtensionFromName(fileName) || ''
    ] ??
      '');

  return new File([blob], fileName, {
    type: effectiveType,
    lastModified
  });
};

export type CloudFileDownloadProgressCallback = (
  loaded: number,
  total: number | undefined
) => void;

const readBodyWithProgress = async (
  response: Response,
  onProgress: CloudFileDownloadProgressCallback
): Promise<Blob> => {
  const contentLengthHeader = response.headers.get('Content-Length');
  const total = contentLengthHeader ? Number(contentLengthHeader) : undefined;
  const contentType = response.headers.get('Content-Type') || '';

  // Streaming reader unsupported (older browsers, opaque responses): fall back
  // to .blob() and emit a single progress event at the end.
  if (!response.body || typeof response.body.getReader !== 'function') {
    const blob = await response.blob();
    onProgress(blob.size, total ?? blob.size);
    return blob;
  }

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;
  onProgress(0, total);

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    if (value) {
      chunks.push(value);
      loaded += value.length;
      onProgress(loaded, total);
    }
  }

  return new Blob(chunks as BlobPart[], contentType ? { type: contentType } : undefined);
};

export const fetchBrowserFileFromUrl = async (
  url: string,
  requestInit: RequestInit | undefined,
  options: BrowserFileOptions,
  onProgress?: CloudFileDownloadProgressCallback
) => {
  const response = await fetch(url, requestInit);
  if (!response.ok) {
    throw new Error(`Failed to download file. Status: ${response.status}`);
  }

  const blob = onProgress
    ? await readBodyWithProgress(response, onProgress)
    : await response.blob();
  if (!blob || blob.size === 0) {
    throw new Error('Downloaded file is empty.');
  }

  return createBrowserFileFromBlob(blob, {
    ...options,
    blobType: blob.type || options.blobType
  });
};
